import { c as _c } from "react/compiler-runtime";
import type { InterfaceToken, Point, Rewards } from '@morpho-org/dapp-core';
import { Avatar, UserPositionIndicator } from '@morpho-org/ui-v6/client';
import { formatMarketName, formatters } from '@morpho-org/ui-v6/utils';
import { Badge, Icon } from '@repo/ui';
import { ArrowDiagonalUp } from '@repo/ui/icons';
import { useMemo } from 'react';
import { InternalLink } from '@/components/common/InternalLink';
import { type Warning, WarningIcon } from '@/components/common/WarningIcon';
import { IconRewardHover } from '../../IconRewardHover';
interface MobileBorrowHeaderProps {
  href?: string;
  prefetch?: boolean;
  onLinkClick?: () => void;
  loanLogoURI?: string | null;
  loanSymbol?: string | null;
  /** Omit both collateral props for idle-liquidity markets — they have no collateral. */
  collateralLogoURI?: string | null;
  collateralSymbol?: string | null;
  lltv: string;
  netApy?: bigint | null;
  apy?: bigint | null;
  rewardAsset?: InterfaceToken | null;
  rewards: Rewards[];
  points: Point[];
  hasUserPosition?: boolean;
  warnings: Warning[];
  /** `supply` for lend positions — flips the rate hover to APY wording. */
  rewardsVariant?: 'market' | 'supply';
}
export default function MobileBorrowHeader(t0) {
  const $ = _c(55);
  const {
    href,
    prefetch,
    onLinkClick,
    loanLogoURI,
    loanSymbol,
    collateralLogoURI,
    collateralSymbol,
    lltv,
    netApy,
    apy,
    rewards,
    points,
    rewardAsset,
    hasUserPosition,
    warnings,
    rewardsVariant: t1
  } = t0;
  const rewardsVariant = t1 === undefined ? "market" : t1;
  let t2;
  if ($[0] !== netApy) {
    t2 = formatters.percent.digits(2).unit("%").max(10).of(netApy, 18);
    $[0] = netApy;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const formattedNetApy = t2;
  let t3;
  if ($[2] !== loanLogoURI) {
    t3 = <Avatar src={loanLogoURI} size="small" />;
    $[2] = loanLogoURI;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== collateralLogoURI || $[5] !== collateralSymbol) {
    t4 = collateralSymbol != null && <Avatar className="ml-[-10px]" src={collateralLogoURI} size="small" />;
    $[4] = collateralLogoURI;
    $[5] = collateralSymbol;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <div className="flex items-center">{t3}{t4}</div>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      lineHeight: "normal",
      maxWidth: 125
    };
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== collateralSymbol || $[12] !== loanSymbol) {
    t7 = formatMarketName(loanSymbol, collateralSymbol);
    $[11] = collateralSymbol;
    $[12] = loanSymbol;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t7) {
    t8 = <span className="text-foreground truncate text-xs" style={t6}>{t7}</span>;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== lltv) {
    t9 = <Badge variant="tertiary" size="sm" className="my-auto">{lltv}</Badge>;
    $[16] = lltv;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== href) {
    t10 = href && <Icon className="text-foreground" icon={<ArrowDiagonalUp />} />;
    $[18] = href;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t5 || $[22] !== t8 || $[23] !== t9) {
    t11 = <div className="gap-2xs flex flex-wrap items-center">{t5}{t8}{t9}{t10}</div>;
    $[20] = t10;
    $[21] = t5;
    $[22] = t8;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const tokenHeader = t11;
  let t12;
  if ($[25] !== href || $[26] !== onLinkClick || $[27] !== prefetch || $[28] !== tokenHeader) {
    t12 = href ? <InternalLink href={href} prefetch={prefetch} onClick={onLinkClick}>{tokenHeader}</InternalLink> : tokenHeader;
    $[25] = href;
    $[26] = onLinkClick;
    $[27] = prefetch;
    $[28] = tokenHeader;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== warnings) {
    t13 = <WarningIcon warnings={warnings} />;
    $[30] = warnings;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== hasUserPosition) {
    t14 = hasUserPosition && <UserPositionIndicator variant="market" />;
    $[32] = hasUserPosition;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t12 || $[35] !== t13 || $[36] !== t14) {
    t15 = <div className="gap-xs flex items-center">{t12}{t13}{t14}</div>;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = BigInt(0);
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== apy || $[40] !== netApy || $[41] !== points || $[42] !== rewardAsset || $[43] !== rewards || $[44] !== rewardsVariant) {
    t17 = {
      netApy,
      apy,
      performanceFee: t16,
      rewards,
      variant: rewardsVariant,
      points,
      asset: rewardAsset
    };
    $[39] = apy;
    $[40] = netApy;
    $[41] = points;
    $[42] = rewardAsset;
    $[43] = rewards;
    $[44] = rewardsVariant;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      display: "flex",
      alignItems: "center",
      lineHeight: "normal"
    };
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== formattedNetApy) {
    t19 = <div className="gap-2xs flex items-center"><span className="text-foreground text-xs" style={t18}>{formattedNetApy}</span></div>;
    $[47] = formattedNetApy;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== t17 || $[50] !== t19) {
    t20 = <IconRewardHover isMobileSupportEnabled={true} RewardsHoverCardProps={t17}>{t19}</IconRewardHover>;
    $[49] = t17;
    $[50] = t19;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== t15 || $[53] !== t20) {
    t21 = <div className="gap-2xs flex items-center justify-between"><div className="flex w-full items-center justify-between">{t15}{t20}</div></div>;
    $[52] = t15;
    $[53] = t20;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  return t21;
}